import { useState, useRef, useEffect, useCallback } from 'react';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, setDoc, serverTimestamp, onSnapshot, getDoc } from 'firebase/firestore';
import { formatDateToKorean } from '../../utils';
import { extractVideoId } from '../../utils';
import { API_BASE_URL, AI_API_URL, TRANSCRIPT_API_URL } from '../../constants';
import { parseErrorType, parseErrorFromResponse, checkDuplicateVideoWithLanguage, checkIsYouTubeShorts } from '../../utils';
import { ERROR_TYPES } from '../../components/Modal/ErrorModal';
import { trackVideoAnalysis, trackVideoAnalysisComplete, trackError } from '../../utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getLanguageByCode } from '../../constants';
import { useErrorHandler } from './useErrorHandler';
import { useProgress } from './useProgress';
import { useHistoryManager } from './useHistoryManager';
import { useYouTubeUtils } from './useYouTubeUtils';
import { useAnalysisStarter } from './useAnalysisStarter';
import { useTranslationProcessor } from './useTranslationProcessor';
import { useChapterProcessor } from './useChapterProcessor';
import { useSummaryProcessor } from './useSummaryProcessor';
import { useQuestionProcessor } from './useQuestionProcessor';
import { useAnalysisProcessor } from './useAnalysisProcessor';

export const useVideo = (userId) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [inputUrl, setInputUrl] = useState('');
  const [inputTitle, setInputTitle] = useState('');
  const [analyzedUrl, setAnalyzedUrl] = useState('');
  const [analyzedTitle, setAnalyzedTitle] = useState('');
  const [analyzedVideoId, setAnalyzedVideoId] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const playerRef = useRef(null);
  

  
  // ✅ 분석 시작 전 로딩 상태 (스피닝 로더용) - 전역 상태
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  
  // ✅ 스트리밍 상태 (핵심 기능 - 먼저 정의)
  const [streamingChapters, setStreamingChapters] = useState([]);
  const [streamingSummary, setStreamingSummary] = useState("");
  const [streamingChunkSummaries, setStreamingChunkSummaries] = useState([]);
  const [streamingTranscript, setStreamingTranscript] = useState("");
  const [streamingSourceLanguage, setStreamingSourceLanguage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [summaryCompleted, setSummaryCompleted] = useState(false);
  const [chaptersCompleted, setChaptersCompleted] = useState(false);
  
  // ✅ 중복 분석 방지 플래그
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null);
  
  // ✅ 영상 길이 정보 상태 (유지)
  const [videoDuration, setVideoDuration] = useState(0);
  
  // ✅ 히스토리 관리 훅 사용 (isStreaming 정의 후)
  const {
    history,
    setHistory,
    editingHistoryId,
    setEditingHistoryId,
    editingTitle,
    setEditingTitle,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetId,
    setDeleteTargetId,
    handleEditHistory,
    handleDeleteClick,
    handleDeleteConfirm
  } = useHistoryManager(userId, isStreaming, selectedIdx, setSelectedIdx, setAnalyzedVideoId, setAnalyzedTitle, setAnalyzedUrl);
  
  // ✅ 진행률 관리 훅 사용
  const {
    progressModal,
    setProgressModal,
    visualProgress,
    globalCopyModal,
    setGlobalCopyModal,
    getMessageByPercentage,
    openProgressModal,
    updateProgress,
    closeProgressModal,
    openGlobalCopyModal,
    closeGlobalCopyModal
  } = useProgress();

  // ✅ 에러 처리 훅 사용
  const {
    errorModalOpen,
    errorType,
    errorMessage,
    failedHistoryId,
    showErrorModal,
    closeErrorModal,
    handleRetry
  } = useErrorHandler(userId, setAnalyzeLoading, setProgressModal, history);

  // ✅ YouTube 유틸리티 훅 사용
  const {
    parseISO8601Duration,
    fetchYoutubeTitle
  } = useYouTubeUtils(setInputTitle, setLoading, setVideoDuration);

  // ✅ 분석 시작 훅 사용
  const {
    handleAnalyze
  } = useAnalysisStarter(
    userId,
    inputUrl,
    setInputUrl,
    setInputTitle,
    loading,
    analyzeLoading,
    setLoading,
    setAnalyzeLoading,
    history,
    showErrorModal,
    parseISO8601Duration,
    setVideoDuration
  );

  // ✅ 분석 처리 훅들 초기화
  const {
    processTranslation
  } = useTranslationProcessor(
    setStreamingTranscript,
    setProgressModal,
    getMessageByPercentage
  );

  const {
    processChapters
  } = useChapterProcessor(
    setStreamingChapters,
    setProgressModal,
    setChaptersCompleted
  );

  const {
    processSummary
  } = useSummaryProcessor(
    setStreamingChunkSummaries,
    setStreamingSummary,
    setProgressModal,
    setSummaryCompleted,
    getMessageByPercentage
  );

  const {
    processQuestions
  } = useQuestionProcessor(
    streamingChunkSummaries,
    userId
  );

  const {
    startAnalysis,
    handleDirectCancel
  } = useAnalysisProcessor(
    userId,
    currentAnalysisId,
    setCurrentAnalysisId,
    setLoading,
    setIsStreaming,
    setSummaryCompleted,
    setChaptersCompleted,
    setStreamingChapters,
    setStreamingSummary,
    setStreamingChunkSummaries,
    setStreamingTranscript,
    setStreamingSourceLanguage,
    setAnalyzedVideoId,
    setAnalyzedTitle,
    setAnalyzedUrl,
    setProgressModal,
    setVideoDuration,
    showErrorModal,
    processTranslation,
    processChapters,
    processSummary,
    processQuestions,
    navigate
  );

  // ✅ 스트리밍 완료 감지 (Firebase 리스너 기반 + 백업 로직)
  useEffect(() => {
    if (summaryCompleted && chaptersCompleted) {
      console.log('🎉 모든 스트리밍 완료 - isStreaming을 false로 설정');
      setIsStreaming(false);
      setCurrentAnalysisId(null);
    }
  }, [summaryCompleted, chaptersCompleted]);
  
  // ✅ 추가 안전장치: 선택된 히스토리가 active 상태면 스트리밍 종료
  useEffect(() => {
    if (isStreaming && selectedIdx >= 0 && history.length > 0) {
      const currentHistory = history[selectedIdx];
      if (currentHistory && currentHistory.status === 'active' && !currentHistory.backgroundProcessing) {
        console.log('🔄 [안전장치] 히스토리 status가 active이므로 스트리밍 종료');
        setIsStreaming(false);
        setCurrentAnalysisId(null);
      }
    }
  }, [isStreaming, selectedIdx, history]);

  // ✅ 반환되는 모든 상태와 함수들
  return {
    // 기본 입력/분석 상태
    inputUrl,
    setInputUrl,
    inputTitle,
    setInputTitle,
    analyzedUrl,
    analyzedTitle,
    analyzedVideoId,
    setAnalyzedVideoId,
    setAnalyzedTitle,
    setAnalyzedUrl,
    
    // 히스토리 관리
    history,
    selectedIdx,
    setSelectedIdx,
    
    // 로딩/에러 상태
    loading,
    error,
    
    // UI 상태
    drawerOpen,
    setDrawerOpen,
    editingHistoryId,
    setEditingHistoryId,
    editingTitle,
    setEditingTitle,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetId,
    setDeleteTargetId,
    
    // 플레이어 참조
    playerRef,
    
    // 핵심 함수들
    fetchYoutubeTitle,
    handleAnalyze,
    handleEditHistory,
    handleDeleteClick,
    handleDeleteConfirm,
    startAnalysis,  // ✅ 분석 시작 함수
    handleDirectCancel, // ✅ 분석 취소 함수
    getMessageByPercentage,  // ✅ 진행률 메시지 함수 추가
    
    // ✅ 스트리밍 상태 (실시간 렌더링용)
    streamingChapters,
    streamingSummary,
    streamingChunkSummaries,
    streamingTranscript,
    isStreaming,
    setStreamingChapters,
    setStreamingSummary,
    setStreamingChunkSummaries,
    setStreamingTranscript,
    
    // ✅ 에러 처리
    errorModalOpen,
    errorType,
    errorMessage,
    closeErrorModal,
    handleRetry,
    
    // ✅ 진행률 모달 (단순화됨)
    progressModal,
    setProgressModal,
    visualProgress, // 시각적 진행률 (부드러운 애니메이션용)
    
    // ✅ 광역 복사용 진행률 모달 상태 추가
    globalCopyModal,
    setGlobalCopyModal,
    
    // 기타
    videoDuration,
    
    // ✅ 분석 시작 전 로딩 상태 (스피닝 로더용)
    analyzeLoading,
    setAnalyzeLoading,
    

  };
}; 